/**
 * 表情回应：工具栏上的选择器 + 屏幕上的飘屏层。
 *
 * 解决的是老系统里一个很实际的问题：学生想说「听懂了」「听不清」，
 * 只能打字，而打字会把大家的注意力从讲课内容上拉走。
 */

import { useEffect, useRef, useState } from 'react'

import { useRoomStore } from '../stores/roomStore'
import { IcSmile } from './Icons'

/** 可选表情。必须是服务端白名单的子集，否则点了会报「不支持的表情」。 */
export const EMOJIS = ['👍', '👏', '❤️', '😂', '😮', '🎉', '🤝', '❓'] as const

/** 工具栏上的表情按钮。 */
export function ReactionPicker() {
  const sendReaction = useRoomStore((s) => s.sendReaction)
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  // 点别处收起面板。会议里工具栏上方就是内容区，
  // 面板赖着不走会挡住老师正在讲的东西。
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  return (
    <div className="tb-react" ref={boxRef}>
      <button
        className={`tb-btn${open ? ' on' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="tb-ico"><IcSmile /></span>
        <span className="tb-txt">表情</span>
      </button>

      {open && (
        <div className="react-pop" role="menu">
          {EMOJIS.map((e) => (
            <button
              key={e}
              className="react-item"
              title={`发送 ${e}`}
              onClick={() => {
                sendReaction(e)
                // 发完就收起：连点同一个表情会被服务端限流，
                // 面板留着只会让人以为没发出去而反复点。
                setOpen(false)
              }}
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** 屏幕右下角的飘屏层。覆盖在内容之上，但不吃鼠标事件。 */
export function ReactionLayer() {
  const reactions = useRoomStore((s) => s.reactions)
  if (reactions.length === 0) return null
  return (
    <div className="react-layer" aria-live="polite">
      {reactions.map((r) => (
        <div key={r.key} className="react-float">
          <span className="react-emoji">{r.emoji}</span>
          <span className="react-who">{r.name}</span>
        </div>
      ))}
    </div>
  )
}
